import React, {ReactElement, useCallback, useContext, useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {TitledBody} from '../../common/components/TitledBody'
import {PhoneUser, ServerPage} from '../../app/types'
import {useSelector} from 'react-redux'
import {callRecordingEnabledSelector, hotDeskEnabledSelector, selectedSiteSelector} from '../../authentication/redux'
import {api, buildPaginatedUrl} from '../../common/api'
import {InfiniteScrollReturnProps, Sort, useInfiniteScroll} from '../../common/components/table/useInfiniteScroll'
import {
    Box,
    Checkbox,
    Divider,
    Grid,
    List,
    ListItem,
    ListItemText,
    Paper,
    Step,
    StepLabel,
    Stepper,
    Typography
} from "@mui/material";
import FormButtons from "../../common/form/FormButtons";
import SpinnableSubmitButton from "../../common/form/SpinnableSubmitButton";
import CancelButton from "../../common/form/CancelButton";
import {ColumnDefinition, InfiniteScrollTable} from "../../common/components/table/Table";
import {sorterHeader} from "../../common/components/table/SorterHeader";
import {formatPhoneNumber} from "../../common/util/formatters";
import {find, reject} from "ramda";
import {Control, FieldError, useController, useForm, useWatch} from "react-hook-form";
import {LanguageForm} from "./LanguageForm";
import FormContainer from "../../common/form/FormContainer";
import {yupResolver} from "@hookform/resolvers/yup";
import {useFormSubmission} from "../../common/servercall/useFormSubmission";
import * as yup from "yup";
import {object} from "yup";
import FormGroup from "../../common/form/FormGroup";
import FormFieldBox from "../../common/form/FormFieldBox";
import TextInput from "../../common/form/TextInput";
import FormRow from "../../common/form/FormRow";
import {useHistory} from "react-router";
import ErrorAlert from "../../common/components/ErrorAlert";
import {LicenseForm} from "./LicenseForm";
import {OutgoingNumberForm} from "./OutgoingNumberForm";
import {UseFormGetFieldState, UseFormSetValue} from "react-hook-form/dist/types/form";
import {AllowChangeOutgoingCliForm} from "./AllowChangeOutgoingCliForm";
import {HideFromPhoneBookCliForm} from "./HideFromPhoneBookForm";
import {HidePhoneStatusForm} from "./HidePhoneStatusForm";
import {NcosForm} from "./NcosForm";
import axios from "axios";
import {WelcomeMessageForm} from "./WelcomeMessageForm";
import {PrioExternalCallsForm} from "./PrioExternalCallsForm";
import {OptimizedFormTeamsForm} from "./OptimizedForTeamsForm";
import {DisableVideoCallsForm} from "./DisableVideoCallsForm";
import {ForwardingScheduleForm} from "./ForwardingScheduleForm";
import {BlfHideCallerForm} from "./BlfHideCallerForm";
import {BlfNotificationDelayTimeForm} from "./BlfNotificationDelayTimeForm";
import {CallRecordingModeForm} from "./CallRecordingModeForm";
import {CallRecordingPlayAnnouncementForm} from "./CallRecordingPlayAnnouncementForm";
import {CallRecordingRecordVoiceMessagingForm} from "./CallRecordingRecordVoiceMessagingForm";
import {AllowedToolsForm} from "./AllowedToolsForm";
import {BlfUsersForm} from "./BlfUsersForm";
import {regexForNumberWithoutEmergencyNr} from "../../common/util/helpers";
import {PhoneBookForm} from "./PhonebookForm";
import {HotDeskingEnabledForm} from "./HotDeskingEnabledForm";
import {HotDeskAssociationLimitForm} from "./HotDeskAssociationLimitForm";
import {AutoFillBlfForm} from "./AutoFillBlfForm";
import {EhsForm} from "./EhsForm";

export interface UserFieldUpdates {
    fieldUpdates: UserFieldUpdate[]
}

interface FormData extends UserFieldUpdates {
    entityIdsToUpdate: string[]
}

export type UserFieldUpdate = {
    fieldName: string | null
    values: UserFieldValue[]
}

export type UserFieldValue = {
    stringValue: string | null
    selected: boolean | null
    stringValues: String[] | any[] | null
}

type ContextProps = {
    formData: FormData
    setFormData: (formData: FormData) => void
}

export const BulkUpdateContext = React.createContext<ContextProps>({
    formData: {entityIdsToUpdate: [], fieldUpdates: []},
    setFormData: (formData: FormData) => {}
})

export const BulkUserUpdate = () => {
    const history = useHistory()
    const [activeStep, setActiveStep] = useState<number>(0);
    const [submitting, setSubmitting] = useState<boolean>(false)
    const [submitError, setSubmitError] = useState<any>(null)
    const [formData, setFormData] = useState<FormData>({
        entityIdsToUpdate: [],
        fieldUpdates: []
    })
    const [atLeastOneUserSelected, setAtLeastOneUserSelected] = useState<boolean>(false)
    const [fileUpload, setFileUpload] = useState<File | null>(null)

    const { t } = useTranslation()
    const site = useSelector(selectedSiteSelector)

    const fetchUsers = useCallback(
        async (pageParam = 0, sortParam: Sort, searchTermParam: string | null): Promise<ServerPage<PhoneUser>> => {
            if (!site) return { content: [], totalPages: 0, number: 0, totalElements: 0 }
            const { data } = await api.get(buildPaginatedUrl(`/api/site/${site.productId}/users`, pageParam, sortParam, searchTermParam, 2000))
            return data
        },
        [site]
    )

    const infiniteScrollProps = useInfiniteScroll<PhoneUser>({
        queryKey: `bulk-users-${site?.productId}`,
        initialSort: { field: 'extension', order: 'asc' },
        fetchFunction: fetchUsers,
    })

    const handleNext = useCallback(async () => {
        if (activeStep === 2) {
            if (!!site) {
                try {
                    setSubmitError(null)
                    setSubmitting(true)

                    if (formData.fieldUpdates[0].fieldName === 'phonebook') {
                        if (!fileUpload) {
                            setSubmitError('select file')
                            setSubmitting(false)
                            return
                        }
                        const formFileData = new FormData()
                        formFileData.set("test", "test")
                        formFileData.append('file', fileUpload)

                        await api.post(`/api/site/${site.productId}/user/bulkPhonebook?clearExisting=${formData.fieldUpdates[0].values[0].selected}&userIds=${formData.entityIdsToUpdate}`, formFileData, {
                            headers: {
                                'Content-Type': 'multipart/form-data'
                            }
                        });
                    } else {
                        await api.put(`/api/site/${site.productId}/user/bulk`,
                            {...formData,
                                fieldUpdates: formData.fieldUpdates.map((update: UserFieldUpdate) => {
                                    return {
                                        fieldName: update.fieldName,
                                        values: update.values.map((value: UserFieldValue) => {
                                            return {
                                                stringValue: value.stringValue,
                                                selected: value.selected,
                                                stringValues: update.fieldName === 'blfUsers'?
                                                    value.stringValues?.map(user => user.uuid)
                                                    : value.stringValues?.filter(s => s)
                                            }
                                        })
                                    }
                                })
                            })
                    }

                    setSubmitting(false)
                    history.push('/users')
                } catch (error) {
                    setSubmitting(false)
                    setSubmitError(error)
                }

            }
        } else {
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
        }
    }, [activeStep, history, site, formData, fileUpload]);


    const handleBack = () => {
        setSubmitError(null)
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const titles = useMemo(() =>
        ['users.bulk-update.step-1', 'users.bulk-update.step-2', 'users.bulk-update.step-3'].map((key, index) => `${index + 1}. ${t(key)}`)
    , [t])

    const buildFormButtons = useCallback((isSubmit: boolean, isSubmitting: boolean) => {
        return <>
            <Grid item >
                <Divider/>
            </Grid>
            <Grid item mt={3} >
                <FormButtons buttons={[<CancelButton
                    title= {t("general.back")}
                    disabled={activeStep === 0}
                    onClick={handleBack}
                />,
                    <SpinnableSubmitButton disabled={!atLeastOneUserSelected} showSpinner={isSubmitting || submitting} label={activeStep === titles.length -1 ? t("general.confirm") : t("general.next")} onClick={isSubmit ? undefined : handleNext}/>
                ]} fullWidth={true}/>
            </Grid>
        </>
    }, [activeStep, atLeastOneUserSelected, t, titles.length, submitting, handleNext])

    const Comps = useMemo(() =>
        <BulkUpdateContext.Provider value={{ formData, setFormData }}>
            <Box hidden={activeStep !== 0} key={0}>
                <Step1Comp {...infiniteScrollProps} allowToContinueChanged={setAtLeastOneUserSelected} buildFormButtons={buildFormButtons} next={handleNext} submitError={submitError} />
            </Box>
            <Box hidden={activeStep !== 1} key={1}>
                <Step2Comp {...infiniteScrollProps} onFileUploaded={setFileUpload} allowToContinueChanged={(allowToContinue: boolean) => {}} buildFormButtons={buildFormButtons} next={handleNext} submitError={submitError} />
            </Box>
            <Box hidden={activeStep !== 2} key={2} >
                <Step3Comp {...infiniteScrollProps} allowToContinueChanged={(allowToContinue: boolean) => {}} buildFormButtons={buildFormButtons} next={handleNext} submitError={submitError} />
            </Box>
        </BulkUpdateContext.Provider>, [infiniteScrollProps, formData, setFormData, activeStep, setAtLeastOneUserSelected, buildFormButtons, handleNext, submitError, setFileUpload])


    return (
        <>
            <TitledBody title={t('users.bulk-update.title')} >
                <Grid container flexWrap={'nowrap'} >
                    <Grid xs={10} container item justifyContent={'space-between'} flexDirection={'column'} spacing={1} flexWrap={'nowrap'}>
                            <Paper elevation={2} sx={{ p: 4, mr: 4}} square={false}>
                                <Grid rowSpacing={4} item container flexDirection={'column'} sx={{height: '100%'}} justifyContent={'flex-end'}  flexWrap={'nowrap'}>
                                    <Grid item sx={{mt: 3}}>
                                        <Typography variant='h6' align='left' gutterBottom>
                                            {titles[activeStep]}
                                        </Typography>
                                    </Grid>
                                    {Comps}
                                </Grid>
                            </Paper>
                    </Grid>
                    <Grid item  >
                        <Stepper activeStep={activeStep} orientation="vertical">
                            {titles.map((title, index) => {
                                return (
                                    <Step key={title} >
                                        <StepLabel >{title}</StepLabel>
                                    </Step>
                                );
                            })}
                        </Stepper>
                    </Grid>
                </Grid>
            </TitledBody>
        </>
    )
}

interface StepProps extends InfiniteScrollReturnProps<PhoneUser>  {
    allowToContinueChanged: ( allowToContinue: boolean) => void
    buildFormButtons: (isSubmit: boolean, isSubmitting: boolean) => ReactElement
    next: () => void
    submitError: any
    onFileUploaded?: (file: File) => void
}

const Step1Comp = (entities: StepProps) => {
    const { t } = useTranslation()
    const [allSelected, setAllSelected] = useState<boolean>(false)

    const { formData, setFormData } = useContext(BulkUpdateContext);

    useEffect(() => {
        entities.allowToContinueChanged(formData.entityIdsToUpdate.length > 0)
    }, [formData.entityIdsToUpdate, entities]);

    const allSelectedChanged = useCallback((selectAll: boolean) => {
        setAllSelected(selectAll)
        if (selectAll) {
            setFormData({...formData, entityIdsToUpdate: entities.rows.map((user: PhoneUser) => user.uuid)})
        } else {
            setFormData({...formData, entityIdsToUpdate: []})
        }
    }, [setAllSelected, entities, formData, setFormData])

    const addOrRemoveEntryToList = useCallback((entry: PhoneUser) => (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            setFormData({...formData, entityIdsToUpdate: [...formData.entityIdsToUpdate, entry.uuid]})
        } else {
            setFormData({...formData, entityIdsToUpdate: reject((v: string) => v === entry.uuid, formData.entityIdsToUpdate)})
        }
    }, [formData, setFormData])

    const columns: ColumnDefinition<PhoneUser>[] = useMemo(
        () => [
            {
                title: '',
                checkbox: {selected: allSelected, setSelected: allSelectedChanged},
                key: 'uuid',
                sorter: false,
                style: {width: '1rem'},
                render: (text: string, record: PhoneUser) => <>{<Checkbox onChange={addOrRemoveEntryToList(record)}
                                                                          checked={!!find(entry => entry === text, formData.entityIdsToUpdate)}/>}</>,
            },
            {
                title: t('users.table.name'),
                key: 'lastName',
                sorter: true,
                render: (text: string, record: PhoneUser) => <>{`${record.lastName} ${record.firstName}`}</>,
            },
            {
                title: t('users.table.extension'),
                key: 'extension',
                sorter: true,
                render: (text: string) => <>{text}</>,
            },
            {
                title: t('users.table.number'),
                key: 'number',
                sorter: true,
                defaultSortOrder: 'ascend' as 'ascend',
                render: (text: string) => <>{formatPhoneNumber(text)}</>,
            },
            {
                title: t('users.table.e-mail'),
                key: 'webexEmail',
                sorter: true,
                render: (text: string) => <>{text}</>,
            },
        ],
        [t, formData.entityIdsToUpdate, addOrRemoveEntryToList, allSelected, allSelectedChanged]
    )

    const sorter = useCallback(col => sorterHeader(col, entities.sort, entities.setSort), [entities.sort, entities.setSort])

    const getRowKey = useCallback(user => user.uuid, [])

    return (
        <Grid container item flexDirection={'column'}>
            <Grid item >
                <InfiniteScrollTable<PhoneUser>
                    hasNextPage={entities.hasNextPage}
                    data={entities.rows}
                    fetchNextPage={entities.fetchNextPage}
                    isFetching={entities.isFetching}
                    getRowKey={getRowKey}
                    sorter={sorter}
                    columns={columns}
                    tableId={'usersBulkUpdatetable'}
                    size={'small'}
                />
            </Grid>
            {entities.buildFormButtons(false, false)}
        </Grid>
    )
}

const schema = yup
    .lazy(({ fieldUpdates }) =>
        yup.object().shape({
            fieldUpdates: yup.array<UserFieldUpdate>().of(
                object().shape({
                    fieldName: yup.string().trim().nullable(),
                    values: yup.array<UserFieldValue>()
                        .when(['fieldName'], {
                            is: (fieldName: string) => fieldName === 'gbtfProfile',
                            then: (schema) => schema.length(1).of(object().shape({
                                stringValue: yup.string().trim().required('required-f'),
                            }))
                        })
                        .when(['fieldName'], {
                            is: (fieldName: string) => fieldName === 'language',
                            then: (schema) => schema.length(1).of(object().shape({
                                stringValue: yup.string().trim().required('required-f'),
                            }))
                        })
                        .when(['fieldName'], {
                            is: (fieldName: string) => fieldName === 'outgoingNumber',
                            then: (schema) => schema.length(1).of(object().shape({
                                stringValue: yup.string().trim().required('required-m'),
                            }))
                        })
                        .when(['fieldName'], {
                            is: (fieldName: string) => fieldName === 'blfNotificationDelayTime',
                            then: (schema) => schema.length(1).of(object().shape({
                                stringValue: yup.number().required('required-m').typeError('invalid-blf-notification-delay').min(0, 'invalid-blf-notification-delay').max(999, 'invalid-blf-notification-delay'),
                            }))
                        })
                        .when(['fieldName'], {
                            is: (fieldName: string) => fieldName === 'forwardNumber',
                            then: (schema) => schema.length(1).of(object().shape({
                                stringValue: yup.string().trim().max(22, 'too-long').matches(regexForNumberWithoutEmergencyNr, {
                                    message: 'invalid-phone-number',
                                    excludeEmptyString: true,
                                }).nullable(),
                            }))
                        })
                        .when(['fieldName'], {
                            is: (fieldName: string) => fieldName === 'hotDeskAssociationLimitHours',
                            then: (schema) => schema.length(1).of(object().shape({
                                stringValue: yup.number()
                                    .transform((value, originalValue) => originalValue === '' || originalValue === null || originalValue === undefined ? null : value)
                                    .typeError('invalid-association-limit-hours').min(0, 'invalid-association-limit-hours').nullable(),
                            }))
                        })

                })
            )
        })
    )


const Step2Comp = (entities: StepProps) => {
    const { t } = useTranslation()

    const callRecordingEnabled = useSelector(callRecordingEnabledSelector)
    const hotDeskEnabled = useSelector(hotDeskEnabledSelector)

    const { formData, setFormData } = useContext(BulkUpdateContext);

    const { control, handleSubmit, setError, setValue, getFieldState, reset } = useForm<UserFieldUpdates>({
        mode: 'onSubmit',
        resolver: yupResolver(schema),
        defaultValues: formData
    })

    const {
        fieldState: { error, invalid },
    } = useController<UserFieldUpdates>({
        name: 'fieldUpdates',
        control,
    })

    const onSubmit = useCallback((fieldUpdates: UserFieldUpdates) => {
        setFormData({...formData, fieldUpdates: fieldUpdates.fieldUpdates});
        entities.next()
    }, [formData, setFormData, entities])

    const { submit, onError } = useFormSubmission<UserFieldUpdates>(onSubmit, setError)

    const fieldOptions = useMemo(
        () =>
            fieldUpdateDefinitions(callRecordingEnabled, hotDeskEnabled, entities.onFileUploaded).map(field => {
                return {
                    value: field.fieldName,
                    label: t(field.fieldLabel),
                    group: t(field.group),
                }
            }),
        [t, callRecordingEnabled, hotDeskEnabled, entities.onFileUploaded]
    )

    const currentField = useWatch({
        control,
        name: 'fieldUpdates.0.fieldName',
    })

    const CurrentFormComp = useMemo(() => {
        const fieldDef = fieldUpdateDefinitions(callRecordingEnabled, hotDeskEnabled, entities.onFileUploaded).find(field => !!field.fieldName && field.fieldName === currentField)

        if (fieldDef) {
            return () => fieldDef.comp(0, control, setValue, getFieldState, error, invalid)
        }
        return () => <Box></Box>
    }, [currentField, control, setValue, getFieldState, callRecordingEnabled, hotDeskEnabled, error, invalid, entities.onFileUploaded])

    return (
        <FormContainer onSubmit={handleSubmit(submit, onError)}>
            <Grid item xs={'auto'} >
                <FormGroup label={''} fullWidth={true}>
                    <FormRow>
                        <FormFieldBox>
                            <TextInput readonly={false}
                                       label={t('users.bulk-update.field-to-update')} name={`fieldUpdates.0.fieldName`}
                                       control={control} required={true}
                                       options={fieldOptions}
                                       onChange={(e, node) =>
                                       {
                                           reset({...formData, fieldUpdates: [{fieldName: e?.target.value , values: [{
                                                        stringValue: '', stringValues: null, selected: false
                                                   }]}]})
                                       }}
                            />
                        </FormFieldBox>
                    </FormRow>
                    <CurrentFormComp />
                </FormGroup>
            </Grid>
            {entities.buildFormButtons(true, false)}
        </FormContainer>
    )
}

const Step3Comp = (entities: StepProps) => {
    const { formData } = useContext(BulkUpdateContext);
    const { t } = useTranslation()
    const callRecordingEnabled = useSelector(callRecordingEnabledSelector)
    const hotDeskEnabled = useSelector(hotDeskEnabledSelector)
    const errorBody = useCallback((errors: any) => {
        if (axios.isAxiosError(errors)) {
            const data = errors?.response?.data as [any]
            if (errors.response?.status === 400 && !!data) {
                const mapPerUser : {
                    [key: string]: string[]
                } = {}

                data.forEach(serverError =>
                    mapPerUser[serverError.context] = [...(!!mapPerUser[serverError.context] ? mapPerUser[serverError.context] : []), t(`general.form-error.${serverError.key}`)]
                )

                return <Box>
                    <Typography variant="subtitle1" component="span" sx={{ mt: 3 }}>
                        {t('users.bulk-update.error')}
                    </Typography>
                    <List>
                        {Object.keys(mapPerUser).map((userId: string) => {
                            return <ListItem key={userId}>
                                <ListItemText
                                    primary={entities.rows.find(user => user.uuid === userId)?.lastName + ' ' + entities.rows.find(user => user.uuid === userId)?.firstName}
                                    secondary={<List dense={true}>
                                        {mapPerUser[userId].map((error: string, index: number) => <ListItem key={`${userId}-${index}`}> <ListItemText primary={error} /></ListItem>)}
                                    </List>}
                                />
                            </ListItem>
                        })}
                    </List>
                </Box>
            } else {
                return <>{t('general.unknown-server-error')}</>
            }
        } else {
            return <>{t('general.unknown-server-error')}</>
        }
    }, [t, entities.rows])

    const ErrorBox = useMemo(() => {
        if (!!entities.submitError) {
            return () => <ErrorAlert showAlert={true} body={errorBody(entities.submitError)} />
        } else {
            return () => <></>
        }
    }, [entities.submitError, errorBody])

    return (
        <>
            <Grid item xs={'auto'} mb={2} >
                <ErrorBox/>
                { !entities.submitError && <Box sx={{ mb: 2, mt: 2 }}>
                        <Typography variant="body1" component="span" >
                            {formData.entityIdsToUpdate && formData.fieldUpdates.length > 0 &&
                                t('users.bulk-update.confirmation-text',
                                    {
                                        nrOfUsers: formData.entityIdsToUpdate.length,
                                        fieldName: t(fieldUpdateDefinitions(callRecordingEnabled, hotDeskEnabled).find(it => it.fieldName === formData.fieldUpdates[0].fieldName)?.fieldLabel || '')
                                    })
                            }
                        </Typography>
                    </Box>
                }
            </Grid>
            {entities.buildFormButtons(false, false)}
        </>
    )
}

const fieldUpdateDefinitions = (callRecordingsEnabled: boolean, hotDeskEnabled: boolean, fileUploaded?: (file: File) => void ) => [
    // General > Info
    {
        fieldName: 'language',
        fieldLabel: 'users.edit.language',
        group: 'users.edit.section.info',
        comp: (index: number, control: Control<any>) => <LanguageForm control={control} index={index} />
    },
    {
        fieldName: 'gbtfProfile',
        fieldLabel: 'users.edit.license',
        group: 'users.edit.section.info',
        comp: (index: number, control: Control<any>) => <LicenseForm control={control} index={index} />
    },
    // General > Numbers
    {
        fieldName: 'outgoingNumber',
        fieldLabel: 'users.edit.outgoing-number',
        group: 'users.edit.section.numbers',
        comp: (index: number, control: Control<any>) => <OutgoingNumberForm control={control} index={index} />
    },
    {
        fieldName: 'allowChangeOutgoingCli',
        fieldLabel: 'users.edit.allow-change-outgoing-number',
        group: 'users.edit.section.numbers',
        comp: (index: number, control: Control<any>,  setValue: UseFormSetValue<any>,
               getFieldState: UseFormGetFieldState<any>) => <AllowChangeOutgoingCliForm control={control} index={index} setValue={setValue} getFieldState={getFieldState}/>
    },
    // General > Privacy
    {
        fieldName: 'hideFromPhoneBook',
        fieldLabel: 'users.edit.hide-from-phonebook',
        group: 'users.edit.section.privacy',
        comp: (index: number, control: Control<any>,  setValue: UseFormSetValue<any>,
               getFieldState: UseFormGetFieldState<any>) => <HideFromPhoneBookCliForm control={control} index={index} setValue={setValue} getFieldState={getFieldState}/>
    },
    {
        fieldName: 'hidePhoneStatus',
        fieldLabel: 'users.edit.hide-phone-status',
        group: 'users.edit.section.privacy',
        comp: (index: number, control: Control<any>,  setValue: UseFormSetValue<any>,
               getFieldState: UseFormGetFieldState<any>) => <HidePhoneStatusForm control={control} index={index} setValue={setValue} getFieldState={getFieldState}/>
    },
    // General > Services
    {
        fieldName: 'ncos',
        fieldLabel: 'users.edit.ncos',
        group: 'users.edit.section.services',
        comp: (index: number, control: Control<any>) => <NcosForm control={control} index={index}/>
    },
    {
        fieldName: 'welcomeMessageFileName',
        fieldLabel: 'flows.edit.welcome-message',
        group: 'users.edit.section.services',
        comp: (index: number, control: Control<any>) => <WelcomeMessageForm control={control} index={index}/>
    },
    {
        fieldName: 'prioExternalCalls',
        fieldLabel: 'users.edit.prio-external-calls',
        group: 'users.edit.section.services',
        comp: (index: number, control: Control<any>,  setValue: UseFormSetValue<any>,
               getFieldState: UseFormGetFieldState<any>) => <PrioExternalCallsForm control={control} index={index} setValue={setValue} getFieldState={getFieldState}/>
    },
    {
        fieldName: 'optimizedForTeams',
        fieldLabel: 'users.edit.optimized-for-teams',
        group: 'users.edit.section.services',
        comp: (index: number, control: Control<any>,  setValue: UseFormSetValue<any>,
               getFieldState: UseFormGetFieldState<any>) => <OptimizedFormTeamsForm control={control} index={index} setValue={setValue} getFieldState={getFieldState}/>
    },
    // General > Devices
    {
        fieldName: 'nonCpeDevices',
        fieldLabel: 'users.edit.allowed-tools',
        group: 'users.edit.section.cpe',
        comp: (index: number, control: Control<any>) => <AllowedToolsForm control={control} index={index} />
    },
    {
        fieldName: 'disableVideo',
        fieldLabel: 'users.edit.disable-video',
        group: 'users.edit.section.cpe',
        comp: (index: number, control: Control<any>,  setValue: UseFormSetValue<any>,
               getFieldState: UseFormGetFieldState<any>) => <DisableVideoCallsForm control={control} index={index} setValue={setValue} getFieldState={getFieldState}/>
    },
    // General > Call recording
    ...(callRecordingsEnabled?
        [
            {
                fieldName: 'callRecordingUserSettings.callRecordingMode',
                fieldLabel: 'call-recording.mode',
                group: 'call-recording.section-header',
                comp: (index: number, control: Control<any>) => <CallRecordingModeForm control={control} index={index}/>
            },
            {
                fieldName: 'callRecordingUserSettings.playAnnouncement',
                fieldLabel: 'call-recording.play-announcement',
                group: 'call-recording.section-header',
                comp: (index: number, control: Control<any>, setValue: UseFormSetValue<any>,
                       getFieldState: UseFormGetFieldState<any>) => <CallRecordingPlayAnnouncementForm control={control} index={index} setValue={setValue} getFieldState={getFieldState}/>
            },
            {
                fieldName: 'callRecordingUserSettings.recordVoiceMessaging',
                fieldLabel: 'call-recording.record-voice-messaging',
                group: 'call-recording.section-header',
                comp: (index: number, control: Control<any>, setValue: UseFormSetValue<any>,
                getFieldState: UseFormGetFieldState<any>) => <CallRecordingRecordVoiceMessagingForm control={control} index={index} setValue={setValue} getFieldState={getFieldState}/>
            },
        ] :
        []),
    // General > Busy lamp field
    {
        fieldName: 'blfUsers',
        fieldLabel: 'users.edit.section.busy-lamp-field',
        group: 'users.edit.section.busy-lamp-field',
        comp: (index: number, control: Control<any>) => <BlfUsersForm control={control} index={index} />
    },
    {
        fieldName: 'blfHideCallerId',
        fieldLabel: 'users.edit.blf-hide-caller-id',
        group: 'users.edit.section.busy-lamp-field',
        comp: (index: number, control: Control<any>,  setValue: UseFormSetValue<any>,
               getFieldState: UseFormGetFieldState<any>) => <BlfHideCallerForm control={control} index={index} setValue={setValue} getFieldState={getFieldState}/>
    },
    {
        fieldName: 'blfNotificationDelayTime',
        fieldLabel: 'users.edit.blf-time-before-pickup-notification',
        group: 'users.edit.section.busy-lamp-field',
        comp: (index: number, control: Control<any>) => <BlfNotificationDelayTimeForm control={control} index={index}/>
    },
    // Time based forwarding
    {
        fieldName: 'forwardScheduleUuid',
        fieldLabel: 'time-based-forwarding.forwarding-schedule',
        group: 'time-based-forwarding.title',
        comp: (index: number, control: Control<any>, setValue: UseFormSetValue<any>, getFieldState: UseFormGetFieldState<any>, error?: FieldError, invalid?: boolean) => <ForwardingScheduleForm setValue={setValue} control={control} index={index} openingHours={false} error={error} invalid={invalid}/>
    },
    {
        fieldName: 'outsideOpeningHoursScheduleUuid',
        fieldLabel: 'time-based-forwarding.openinghours-schedule',
        group: 'time-based-forwarding.title',
        comp: (index: number, control: Control<any>,  setValue: UseFormSetValue<any>, getFieldState: UseFormGetFieldState<any>, error?: FieldError, invalid?: boolean) => <ForwardingScheduleForm setValue={setValue} control={control} index={index} openingHours={true} error={error} invalid={invalid}/>
    },
    // Phonebook
    {
        fieldName: 'phonebook',
        fieldLabel: 'navigation.phonebook',
        group: 'navigation.phonebook',
        comp: (index: number, control: Control<any>, setValue: UseFormSetValue<any>,
               getFieldState: UseFormGetFieldState<any>) => <PhoneBookForm onFileUploaded={fileUploaded}
            control={control} index={index} setValue={setValue} getFieldState={getFieldState}/>
    },
    // Hot desk
    ...(hotDeskEnabled?
        [
            {
                fieldName: 'hotDeskingEnabled',
                fieldLabel: 'users.edit.hot-desking',
                group: 'users.sections.hot-desk',
                comp: (index: number, control: Control<any>, setValue: UseFormSetValue<any>,
                       getFieldState: UseFormGetFieldState<any>) => <HotDeskingEnabledForm control={control} index={index} setValue={setValue} getFieldState={getFieldState}/>
            },
            {
                fieldName: 'hotDeskAssociationLimitEnabled',
                fieldLabel: 'users.edit.association-limit',
                group: 'users.sections.hot-desk',
                comp: (index: number, control: Control<any>, setValue: UseFormSetValue<any>,
                       getFieldState: UseFormGetFieldState<any>) => <HotDeskAssociationLimitForm control={control} index={index} setValue={setValue} getFieldState={getFieldState}/>
            },
            {
                fieldName: 'autoFillBlf',
                fieldLabel: 'cpes.config.MPP_AUTOFILL_BLF',
                group: 'users.sections.hot-desk',
                comp: (index: number, control: Control<any>, setValue: UseFormSetValue<any>,
                       getFieldState: UseFormGetFieldState<any>) => <AutoFillBlfForm control={control} index={index} setValue={setValue} getFieldState={getFieldState}/>
            },
            {
                fieldName: 'ehs',
                fieldLabel: 'cpes.config.MPP_EHS',
                group: 'users.sections.hot-desk',
                comp: (index: number, control: Control<any>, setValue: UseFormSetValue<any>,
                       getFieldState: UseFormGetFieldState<any>) => <EhsForm control={control} index={index} setValue={setValue} getFieldState={getFieldState}/>
            },
        ] :
        [])
]


